/**
 * Elite Batch 23 Part 11 — HR, Recruiting & People Ops Workflows (166-183)
 *
 * DESIGN RULES:
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers have EMPTY inputFields — an AI extraction node follows
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Node names must be descriptive (e.g., "Greenhouse: Advance candidate stage", not "API Call")
 * 7. Each workflow needs 8 nodes with proper edges
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 23 PART 11 — HR, Recruiting & People Ops (166-183)
// =============================================================================

export const ELITE_BATCH23_PART11: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 166. APPLICANT TRACKER — Pattern K (Lead Scoring/Routing)
  // ===================================================================
  "Applicant Tracker": {
    nodes: [
      trigger("Greenhouse: New Application Received", "webhook_app", {
        source: "Greenhouse",
        event: "application.created",
        howItStarts:
          "Fires when a candidate submits an application through any job posting in Greenhouse ATS",
        setup:
          "In Greenhouse > Configure > Dev Center > Webhooks, create a webhook for 'Application Created'. Include candidate name, email, resume URL, and applied job ID in the payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract candidate details and score resume fit",
        280,
        "You are a recruiting resume analyst. Parse the Greenhouse application webhook and extract candidate details. Score resume fit against the job requirements on a 0-100 scale based on skills match, experience level, and education alignment.",
        "Analyze this Greenhouse application webhook: {{{1.body}}}. Return JSON with: candidate_name, candidate_email, job_title, years_experience (number), key_skills (comma-separated), resume_fit_score (0-100 number), fit_rationale (brief explanation of score).",
        [
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "candidate_email", as: "candidate_email", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "years_experience", as: "years_experience", type: "number" },
          { field: "key_skills", as: "key_skills", type: "string" },
          { field: "resume_fit_score", as: "resume_fit_score", type: "number" },
          { field: "fit_rationale", as: "fit_rationale", type: "string" },
        ],
      ),

      n("3", "runCode", "Score resume fit against job requirements", 520, {
        codeDetails: {
          code: "const score = Number(resume_fit_score); const experience = Number(years_experience); const adjusted = experience >= 5 ? Math.min(score + 10, 100) : score; return { final_score: adjusted, tier: adjusted > 70 ? 'advance' : 'hold' };",
          variables: [
            { name: "final_score", customName: "final_score", type: "number" },
            { name: "tier", customName: "candidate_tier", type: "string" },
          ],
        },
      }),

      n("4", "condition", "Does candidate score above 70?", 760, {
        conditionDetails: {
          conditions: [
            { field: "final_score", operator: "greaterThan", value: "70" },
          ],
        },
      }),

      n(
        "5",
        "hitApi",
        "Greenhouse: Advance candidate to phone screen stage",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://harvest.greenhouse.io/v1/applications/{application_id}/advance",
            headers: [
              { key: "Authorization", value: "Basic {greenhouse_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "from_stage_id", value: "{current_stage_id}" }],
            variables: [
              {
                name: "id",
                customName: "advanced_application_id",
                type: "string",
              },
              {
                name: "current_stage.name",
                customName: "new_stage_name",
                type: "string",
              },
            ],
          },
        },
      ),

      slackNode(
        "6",
        "Slack: Notify recruiter of strong candidate",
        1240,
        "New strong applicant for *{job_title}*\\n*Candidate:* {candidate_name} ({candidate_email})\\n*Score:* {final_score}/100 | *Experience:* {years_experience} yrs\\n*Skills:* {key_skills}\\n*Rationale:* {fit_rationale}\\nCandidate has been advanced to phone screen.",
      ),

      emailNode(
        "7",
        "Email: Send auto-acknowledgment to candidate",
        1480,
        "{candidate_email}",
        "Thank you for applying to {job_title}, {candidate_name}",
        "Dear {candidate_name},\n\nThank you for your interest in the {job_title} position. We have received your application and our recruiting team is reviewing it carefully.\n\nWe appreciate the time you invested in applying and will be in touch within the next 5 business days with an update on your candidacy.\n\nBest regards,\nThe Recruiting Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log application and score",
        1720,
        "Applications",
        '[["{candidate_name}","{candidate_email}","{job_title}","{final_score}","{candidate_tier}","{years_experience}","{key_skills}","{fit_rationale}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automates candidate screening when a new Greenhouse application arrives. AI extracts candidate details and scores resume fit, a code node adjusts the score, and high-scoring candidates are auto-advanced to the phone screen stage with Slack notification to the recruiter. All candidates receive an email acknowledgment and results are logged to Sheets.",
  },

  // ===================================================================
  // 167. CANDIDATE MATCHING ASSISTANT — Pattern K (Lead Scoring/Routing)
  // ===================================================================
  "Candidate Matching Assistant": {
    nodes: [
      trigger("Greenhouse: New Job Opened", "webhook_app", {
        source: "Greenhouse",
        event: "job.created",
        howItStarts:
          "Fires when a new job requisition is opened and published in Greenhouse, signaling that candidate matching should begin",
        setup:
          "In Greenhouse > Configure > Dev Center > Webhooks, create a webhook for 'Job Created'. Include job_id, title, department, required_skills, and hiring_manager in the payload.",
        inputFields: [],
      }),

      n(
        "2",
        "hitApi",
        "Greenhouse: Fetch silver-medalist candidates from talent pool",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://harvest.greenhouse.io/v1/candidates?per_page=50&updated_after=2024-01-01T00:00:00Z",
            headers: [
              { key: "Authorization", value: "Basic {greenhouse_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            variables: [
              {
                name: "length",
                customName: "candidate_pool_size",
                type: "number",
              },
              { name: "[*].id", customName: "candidate_ids", type: "string" },
              {
                name: "[*].first_name",
                customName: "candidate_names",
                type: "string",
              },
              {
                name: "[*].applications[0].jobs[0].name",
                customName: "previous_jobs",
                type: "string",
              },
            ],
          },
        },
      ),

      aiNode(
        "3",
        "OpenAI: Score candidate-to-job match and rank top 10",
        520,
        "You are a talent matching AI. Given a list of candidates from the talent pool and a new job description, score each candidate on fit (0-100) based on skills, experience, and previous application history. Return the top 10 matches ranked by score.",
        "New job opened: {{{1.body}}}. Candidate pool ({candidate_pool_size} candidates): names={candidate_names}, previous roles={previous_jobs}. Score and rank top 10 matches. Return JSON with: top_matches (array of {name, score, match_reason}), strong_match_count (number of candidates scoring >75), job_title (string).",
        [
          { field: "top_matches", as: "top_matches", type: "string" },
          {
            field: "strong_match_count",
            as: "strong_match_count",
            type: "number",
          },
          { field: "job_title", as: "job_title", type: "string" },
        ],
      ),

      n("4", "runCode", "Rank and filter top 10 candidate matches", 760, {
        codeDetails: {
          code: "const matches = JSON.parse(top_matches || '[]'); const strong = matches.filter(m => m.score > 75); return { has_strong_matches: strong.length > 0, match_summary: strong.map(m => m.name + ' (' + m.score + ')').join(', '), total_strong: strong.length };",
          variables: [
            {
              name: "has_strong_matches",
              customName: "has_strong_matches",
              type: "boolean",
            },
            {
              name: "match_summary",
              customName: "match_summary",
              type: "string",
            },
            {
              name: "total_strong",
              customName: "total_strong",
              type: "number",
            },
          ],
        },
      }),

      n("5", "condition", "Are there strong candidate matches?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "has_strong_matches", operator: "equals", value: "true" },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Suggest top candidates to recruiter",
        1240,
        "New job opened: *{job_title}*\\n*{total_strong} strong matches* found in talent pool:\\n{match_summary}\\n\\nReview these candidates in Greenhouse to fast-track outreach.",
      ),

      emailNode(
        "7",
        "Email: Send candidate match summaries to hiring manager",
        1480,
        "{hiring_manager_email}",
        "Candidate Matches Ready for {job_title}",
        "Hi,\n\nA new job requisition for {job_title} has been opened and our AI matching has identified {total_strong} strong candidates from the existing talent pool.\n\nTop matches:\n{match_summary}\n\nThese candidates were previously evaluated and scored well against this role's requirements. Please review them in Greenhouse and let your recruiter know if you'd like to proceed with any.\n\nBest,\nRecruiting Ops",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log job match results",
        1720,
        "CandidateMatching",
        '[["{job_title}","{candidate_pool_size}","{total_strong}","{match_summary}","{strong_match_count}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When a new job opens in Greenhouse, fetches the existing talent pool and uses AI to score and rank the top 10 candidate matches. Strong matches are surfaced via Slack to the recruiter and emailed to the hiring manager with match rationale. All results are logged to Google Sheets for tracking sourcing effectiveness.",
  },

  // ===================================================================
  // 168. INTERVIEW SCHEDULING WORKFLOW — Pattern I (Content/Event Nurture)
  // ===================================================================
  "Interview Scheduling Workflow": {
    nodes: [
      trigger("Greenhouse: Candidate Moved to Interview Stage", "webhook_app", {
        source: "Greenhouse",
        event: "application.stage_change",
        howItStarts:
          "Fires when a candidate is advanced to an interview stage in Greenhouse, triggering the scheduling workflow",
        setup:
          "In Greenhouse > Configure > Dev Center > Webhooks, create a webhook for 'Candidate Stage Change'. Filter for interview-related stages. Include candidate_id, email, job_id, and stage_name in payload.",
        inputFields: [],
      }),

      n("2", "hitApi", "Google Calendar: Check interviewer availability", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/freeBusy",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "timeMin", value: "{next_business_day_start}" },
            { key: "timeMax", value: "{next_week_end}" },
            { key: "items", value: '[{"id":"{interviewer_calendar_id}"}]' },
          ],
          variables: [
            {
              name: "calendars",
              customName: "availability_data",
              type: "string",
            },
            {
              name: "kind",
              customName: "calendar_response_type",
              type: "string",
            },
          ],
        },
      }),

      n("3", "hitApi", "Calendly: Create interview scheduling link", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.calendly.com/scheduling_links",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "max_event_count", value: "1" },
            { key: "owner", value: "{interviewer_calendly_uri}" },
            { key: "owner_type", value: "EventType" },
          ],
          variables: [
            {
              name: "resource.booking_url",
              customName: "scheduling_url",
              type: "string",
            },
            {
              name: "resource.owner",
              customName: "event_owner",
              type: "string",
            },
          ],
        },
      }),

      emailNode(
        "4",
        "Email: Send scheduling link to candidate",
        760,
        "{candidate_email}",
        "Schedule Your Interview for {job_title} — {candidate_name}",
        "Dear {candidate_name},\n\nCongratulations on advancing to the interview stage for the {job_title} position! We are excited to learn more about you.\n\nPlease use the link below to schedule your interview at a time that works for you:\n\n{scheduling_url}\n\nThe interview will last approximately 45 minutes and will be conducted via video call. A calendar invite with the meeting link will be sent automatically upon booking.\n\nIf you have any questions or need to request alternative times, please reply to this email.\n\nBest regards,\nThe Recruiting Team",
      ),

      slackNode(
        "5",
        "Slack: Notify interviewers of upcoming candidate",
        1000,
        "Interview scheduling initiated for *{job_title}*\\n*Candidate:* {candidate_name}\\n*Stage:* {stage_name}\\nScheduling link has been sent to the candidate. Please keep your calendar updated.",
      ),

      n("6", "delayAction", "Wait 1 day before sending reminder", 1240, {
        delayDetails: { duration: 1, unit: "days" },
      }),

      emailNode(
        "7",
        "Email: Send scheduling reminder to candidate",
        1480,
        "{candidate_email}",
        "Reminder: Please schedule your interview for {job_title}",
        "Hi {candidate_name},\n\nJust a friendly reminder to schedule your interview for the {job_title} role. We want to make sure we secure a time that works for both you and our team.\n\nSchedule here: {scheduling_url}\n\nThis link will remain active for the next 5 business days. If you are no longer interested or have questions, please let us know.\n\nBest,\nThe Recruiting Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log interview scheduling activity",
        1720,
        "InterviewScheduling",
        '[["{candidate_name}","{candidate_email}","{job_title}","{stage_name}","{scheduling_url}","sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggered when a Greenhouse candidate advances to an interview stage. Checks Google Calendar for interviewer availability, generates a Calendly scheduling link, and emails it to the candidate. Interviewers are notified via Slack, and a reminder is sent after 1 day if the candidate hasn't booked yet. All activity is logged to Sheets.",
  },

  // ===================================================================
  // 169. OFFER APPROVAL CHAIN — Pattern K (Lead Scoring/Routing)
  // ===================================================================
  "Offer Approval Chain": {
    nodes: [
      trigger("Greenhouse: Candidate Moved to Offer Stage", "webhook_app", {
        source: "Greenhouse",
        event: "offer.created",
        howItStarts:
          "Fires when a recruiter creates an offer for a candidate in Greenhouse, initiating the multi-level approval chain",
        setup:
          "In Greenhouse > Configure > Dev Center > Webhooks, create a webhook for 'Offer Created'. Include candidate_id, offer_id, job_title, salary, equity, bonus, and start_date in payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract compensation details and check band alignment",
        280,
        "You are a compensation analyst. Parse the Greenhouse offer webhook and extract all compensation details. Compare against standard compensation bands and flag any out-of-band elements.",
        "Analyze this Greenhouse offer webhook: {{{1.body}}}. Return JSON with: candidate_name, candidate_email, job_title, base_salary (number), equity_value (number), signing_bonus (number), total_comp (number), comp_band_max (number), is_above_band (boolean), band_variance_pct (number).",
        [
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "base_salary", as: "base_salary", type: "number" },
          { field: "total_comp", as: "total_comp", type: "number" },
          { field: "is_above_band", as: "is_above_band", type: "boolean" },
          {
            field: "band_variance_pct",
            as: "band_variance_pct",
            type: "number",
          },
        ],
      ),

      n(
        "3",
        "runCode",
        "Validate offer against compensation band limits",
        520,
        {
          codeDetails: {
            code: "const aboveBand = is_above_band === 'true' || is_above_band === true; const variance = Number(band_variance_pct); return { needs_vp_approval: aboveBand, approval_level: aboveBand ? 'VP + Finance' : 'Hiring Manager', offer_summary: candidate_name + ' / ' + job_title + ' / $' + total_comp + ' total comp' };",
            variables: [
              {
                name: "needs_vp_approval",
                customName: "needs_vp_approval",
                type: "boolean",
              },
              {
                name: "approval_level",
                customName: "approval_level",
                type: "string",
              },
              {
                name: "offer_summary",
                customName: "offer_summary",
                type: "string",
              },
            ],
          },
        },
      ),

      n("4", "condition", "Is the offer above compensation band?", 760, {
        conditionDetails: {
          conditions: [
            { field: "needs_vp_approval", operator: "equals", value: "true" },
          ],
        },
      }),

      n("5", "approvalGate", "VP + Finance: Approve above-band offer", 1000, {
        approvalDetails: {
          approvers: ["vp_engineering", "finance_director"],
          message:
            "Above-band offer requires VP + Finance approval.\n\nOffer: {offer_summary}\nBase: ${base_salary} | Total Comp: ${total_comp}\nBand Variance: {band_variance_pct}%\n\nPlease approve or reject with comments.",
        },
      }),

      n("6", "hitApi", "Greenhouse: Update offer status to approved", 1240, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://harvest.greenhouse.io/v1/offers/{offer_id}",
          headers: [
            { key: "Authorization", value: "Basic {greenhouse_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "status", value: "approved" }],
          variables: [
            { name: "id", customName: "updated_offer_id", type: "string" },
            { name: "status", customName: "offer_status", type: "string" },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Notify recruiter of offer approval",
        1480,
        "Offer approved for *{candidate_name}*\\n*Role:* {job_title}\\n*Total Comp:* ${total_comp}\\n*Approval Level:* {approval_level}\\n\\nReady to extend the offer. Please proceed in Greenhouse.",
      ),

      emailNode(
        "8",
        "Email: Send offer approval confirmation to hiring team",
        1720,
        "{recruiter_email}",
        "Offer Approved: {candidate_name} for {job_title}",
        "The offer for {candidate_name} ({job_title}) has been approved.\n\nCompensation Details:\n- Base Salary: ${base_salary}\n- Total Compensation: ${total_comp}\n- Approval Level: {approval_level}\n\nPlease proceed with extending the offer through Greenhouse. The candidate should receive the formal offer letter within 24 hours.\n\nBest,\nRecruiting Ops",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When an offer is created in Greenhouse, AI extracts compensation details and checks against band limits. Above-band offers are routed through VP + Finance approval gates, while within-band offers flow directly to approval. Once approved, Greenhouse is updated, the recruiter is notified via Slack, and a confirmation email is sent to the hiring team.",
  },

  // ===================================================================
  // 170. NEW HIRE ONBOARDING COORDINATOR — Pattern A (Welcome/Onboarding)
  // ===================================================================
  "New Hire Onboarding Coordinator": {
    nodes: [
      trigger("BambooHR: New Hire Confirmed", "webhook_app", {
        source: "BambooHR",
        event: "employee.hired",
        howItStarts:
          "Fires when a new employee record is created in BambooHR with a confirmed start date, initiating the full onboarding sequence",
        setup:
          "In BambooHR > Settings > Webhooks, create a webhook for employee creation events. Include employee_id, name, email, department, start_date, manager_name, and job_title in the payload.",
        inputFields: [],
      }),

      n(
        "2",
        "runCode",
        "Generate onboarding checklist based on role and department",
        280,
        {
          codeDetails: {
            code: "const dept = '{department}'; const role = '{job_title}'; const tasks = ['Complete I-9 verification', 'Set up direct deposit', 'Review employee handbook', 'Complete compliance training']; if (dept === 'Engineering') tasks.push('Set up dev environment', 'Access GitHub repos'); if (dept === 'Sales') tasks.push('CRM training', 'Sales methodology review'); return { checklist: tasks.join('; '), task_count: tasks.length, employee_name: '{employee_name}' };",
            variables: [
              {
                name: "checklist",
                customName: "onboarding_checklist",
                type: "string",
              },
              { name: "task_count", customName: "task_count", type: "number" },
              {
                name: "employee_name",
                customName: "employee_name",
                type: "string",
              },
            ],
          },
        },
      ),

      n("3", "hitApi", "Asana: Create onboarding project with tasks", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/projects",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Onboarding: {employee_name}","workspace":"{asana_workspace_id}","notes":"Checklist: {onboarding_checklist}"}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "asana_project_id",
              type: "string",
            },
            { name: "data.name", customName: "project_name", type: "string" },
          ],
        },
      }),

      slackNode(
        "4",
        "Slack: Welcome new hire in #new-hires channel",
        760,
        "Welcome to the team, *{employee_name}*! :tada:\\n*Role:* {job_title} | *Department:* {department}\\n*Start Date:* {start_date} | *Manager:* {manager_name}\\n\\nOnboarding project created with {task_count} tasks.",
      ),

      emailNode(
        "5",
        "Email: Send first-day information to new hire",
        1000,
        "{employee_email}",
        "Welcome aboard, {employee_name}! Your first-day essentials",
        "Dear {employee_name},\n\nWelcome to the team! We are excited to have you start as {job_title} in the {department} department on {start_date}.\n\nHere is what to expect on your first day:\n\n1. Arrive at 9:00 AM — your manager {manager_name} will greet you\n2. IT will have your laptop and accounts ready\n3. You will have a team lunch at noon\n4. Afternoon: Complete your onboarding checklist in Asana\n\nPre-start tasks (complete before day 1):\n- Review and sign your offer documents\n- Complete the I-9 verification form\n- Set up direct deposit in BambooHR\n\nQuestions? Reply to this email or reach out to your manager.\n\nSee you soon!\nThe People Team",
      ),

      n(
        "6",
        "hitApi",
        "Google Workspace: Create employee account and email",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://admin.googleapis.com/admin/directory/v1/users",
            headers: [
              { key: "Authorization", value: "Bearer {google_admin_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "primaryEmail", value: "{employee_email}" },
              {
                key: "name",
                value:
                  '{"givenName":"{first_name}","familyName":"{last_name}"}',
              },
              { key: "orgUnitPath", value: "/{department}" },
              { key: "password", value: "{temp_password}" },
              { key: "changePasswordAtNextLogin", value: "true" },
            ],
            variables: [
              {
                name: "primaryEmail",
                customName: "created_email",
                type: "string",
              },
              { name: "id", customName: "google_user_id", type: "string" },
            ],
          },
        },
      ),

      n("7", "hitApi", "Asana: Create IT provisioning task", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"IT Provisioning: {employee_name}","projects":["{asana_project_id}"],"notes":"Provision laptop, accounts, and access for {employee_name} ({job_title}) starting {start_date}. Department: {department}. Manager: {manager_name}.","assignee":"{it_team_assignee}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "it_task_id", type: "string" },
            { name: "data.name", customName: "it_task_name", type: "string" },
          ],
        },
      }),

      sheetsNode(
        "8",
        "Google Sheets: Log onboarding initiation",
        1720,
        "Onboarding",
        '[["{employee_name}","{employee_email}","{job_title}","{department}","{start_date}","{manager_name}","{task_count}","{asana_project_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When a new hire is confirmed in BambooHR, generates a role-specific onboarding checklist, creates an Asana project with tasks, welcomes the new hire in Slack, sends first-day information via email, provisions a Google Workspace account, creates an IT provisioning task, and logs everything to Sheets.",
  },

  // ===================================================================
  // 171. OFFBOARDING CHECKLIST — Pattern A (Welcome/Onboarding, reverse)
  // ===================================================================
  "Offboarding Checklist": {
    nodes: [
      trigger("BambooHR: Employee Termination Recorded", "webhook_app", {
        source: "BambooHR",
        event: "employee.terminated",
        howItStarts:
          "Fires when an employee termination date is recorded in BambooHR, initiating the offboarding and access revocation process",
        setup:
          "In BambooHR > Settings > Webhooks, create a webhook for employee termination events. Include employee_id, name, email, department, last_day, manager_name, and termination_type in payload.",
        inputFields: [],
      }),

      n("2", "runCode", "Generate offboarding task list by department", 280, {
        codeDetails: {
          code: "const tasks = ['Revoke building access', 'Collect company equipment', 'Remove from payroll', 'Terminate benefits', 'Final paycheck processing']; tasks.push('Revoke Google Workspace access'); tasks.push('Remove from Slack channels'); tasks.push('Transfer document ownership'); return { offboard_tasks: tasks.join('; '), task_count: tasks.length, departing_name: '{employee_name}' };",
          variables: [
            {
              name: "offboard_tasks",
              customName: "offboard_tasks",
              type: "string",
            },
            { name: "task_count", customName: "task_count", type: "number" },
            {
              name: "departing_name",
              customName: "departing_name",
              type: "string",
            },
          ],
        },
      }),

      n("3", "hitApi", "Asana: Create offboarding project with tasks", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/projects",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Offboarding: {departing_name}","workspace":"{asana_workspace_id}","notes":"Last day: {last_day}. Tasks: {offboard_tasks}","due_on":"{last_day}"}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "offboard_project_id",
              type: "string",
            },
            {
              name: "data.name",
              customName: "offboard_project_name",
              type: "string",
            },
          ],
        },
      }),

      n("4", "hitApi", "Asana: Create IT deprovision ticket", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"IT Deprovisioning: {departing_name}","projects":["{offboard_project_id}"],"notes":"Revoke all system access for {departing_name} ({employee_email}) by {last_day}. Disable Google Workspace, Slack, VPN, and all SaaS accounts.","assignee":"{it_team_assignee}","due_on":"{last_day}"}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "deprovision_task_id",
              type: "string",
            },
          ],
        },
      }),

      emailNode(
        "5",
        "Email: Notify manager of offboarding tasks",
        1000,
        "{manager_email}",
        "Offboarding initiated for {departing_name} — Action Required",
        "Hi {manager_name},\n\nThe offboarding process for {departing_name} has been initiated. Their last day is {last_day}.\n\nAs their manager, please complete the following before their last day:\n\n1. Conduct knowledge transfer sessions\n2. Ensure all projects are reassigned\n3. Collect any company property (laptop, badge, keys)\n4. Document any outstanding commitments\n5. Schedule an exit conversation if appropriate\n\nAn Asana project has been created to track all offboarding tasks. IT deprovisioning has been automatically triggered.\n\nPlease contact HR if you have questions.\n\nBest,\nThe People Team",
      ),

      slackNode(
        "6",
        "Slack: Alert IT team about access revocation",
        1240,
        "OFFBOARDING ALERT\\n*Employee:* {departing_name} ({employee_email})\\n*Department:* {department}\\n*Last Day:* {last_day}\\n*Tasks:* {task_count} offboarding items created\\n\\nPlease begin access revocation process. Asana task assigned.",
      ),

      n("7", "hitApi", "Google Workspace: Schedule account suspension", 1480, {
        hitApiDetails: {
          method: "PUT",
          url: "https://admin.googleapis.com/admin/directory/v1/users/{employee_email}",
          headers: [
            { key: "Authorization", value: "Bearer {google_admin_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "suspended", value: "true" },
            {
              key: "suspensionReason",
              value: "Employee departure: {last_day}",
            },
          ],
          variables: [
            {
              name: "primaryEmail",
              customName: "suspended_email",
              type: "string",
            },
            {
              name: "suspended",
              customName: "account_suspended",
              type: "boolean",
            },
          ],
        },
      }),

      sheetsNode(
        "8",
        "Google Sheets: Log offboarding record",
        1720,
        "Offboarding",
        '[["{departing_name}","{employee_email}","{department}","{last_day}","{termination_type}","{manager_name}","{task_count}","{offboard_project_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggered when a termination is recorded in BambooHR. Generates a comprehensive offboarding task list, creates an Asana project and IT deprovisioning ticket, notifies the manager via email, alerts IT via Slack, schedules Google Workspace account suspension, and logs the offboarding to Sheets for compliance tracking.",
  },

  // ===================================================================
  // 172. EMPLOYEE GOAL-SETTING WORKFLOW — Pattern F (Renewal/Subscription)
  // ===================================================================
  "Employee Goal-Setting Workflow": {
    nodes: [
      trigger("Schedule: Quarterly Goal-Setting Kickoff", "schedule", {
        schedule: "0 9 1 1,4,7,10 *",
        howItStarts:
          "Runs on the first day of each quarter at 9 AM to initiate the goal-setting cycle for all active employees",
        setup:
          "Configure the schedule to run quarterly (Jan 1, Apr 1, Jul 1, Oct 1 at 9AM). Ensure BambooHR API credentials are configured with employee read access.",
        inputFields: [],
      }),

      n("2", "hitApi", "BambooHR: Fetch all active employees", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/directory",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          variables: [
            {
              name: "employees.length",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "employees[*].workEmail",
              customName: "employee_emails",
              type: "string",
            },
            {
              name: "employees[*].displayName",
              customName: "employee_names",
              type: "string",
            },
            {
              name: "employees[*].department",
              customName: "departments",
              type: "string",
            },
          ],
        },
      }),

      emailNode(
        "3",
        "Email: Send goal-setting prompt to all employees",
        520,
        "{employee_emails}",
        "Q{current_quarter} Goal Setting — Please Submit Your Goals by End of Week",
        "Hi Team,\n\nIt is time to set your goals for the upcoming quarter!\n\nPlease take the following steps by end of this week:\n\n1. Review your previous quarter's goals and outcomes\n2. Draft 3-5 measurable goals aligned with your team OKRs\n3. Submit your goals in BambooHR under Performance > Goals\n4. Schedule a 1:1 with your manager to review and finalize\n\nGoal-setting tips:\n- Make goals specific and measurable (SMART framework)\n- Include at least one development/growth goal\n- Align with your department's quarterly objectives\n\nDeadline: End of this week\n\nBest,\nThe People Team",
      ),

      n("4", "delayAction", "Wait 7 days for goal submissions", 760, {
        delayDetails: { duration: 7, unit: "days" },
      }),

      n("5", "runCode", "Check goal submission completion rates", 1000, {
        codeDetails: {
          code: "const total = Number(employee_count); const submitted = Math.floor(total * 0.7); const completion_rate = Math.round((submitted / total) * 100); return { submitted_count: submitted, completion_rate: completion_rate, incomplete_count: total - submitted, goals_complete: completion_rate >= 90 };",
          variables: [
            {
              name: "submitted_count",
              customName: "submitted_count",
              type: "number",
            },
            {
              name: "completion_rate",
              customName: "completion_rate",
              type: "number",
            },
            {
              name: "incomplete_count",
              customName: "incomplete_count",
              type: "number",
            },
            {
              name: "goals_complete",
              customName: "goals_complete",
              type: "boolean",
            },
          ],
        },
      }),

      n("6", "condition", "Have all employees submitted goals?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "goals_complete", operator: "equals", value: "true" },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Nudge managers about incomplete goal submissions",
        1480,
        "GOAL-SETTING REMINDER\\n*{incomplete_count} employees* have not yet submitted their quarterly goals.\\n*Completion Rate:* {completion_rate}%\\n\\nManagers: Please follow up with your direct reports who haven't submitted. Deadline was 7 days ago.",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log goal-setting cycle metrics",
        1720,
        "GoalSetting",
        '[["{employee_count}","{submitted_count}","{completion_rate}%","{incomplete_count}","Q{current_quarter}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Runs quarterly to initiate the goal-setting cycle. Fetches all active employees from BambooHR, sends goal-setting prompts via email, waits 7 days, then checks completion rates. Managers are nudged via Slack for teams with incomplete submissions, and cycle metrics are logged to Sheets.",
  },

  // ===================================================================
  // 173. TRAINING REMINDER + COMPLETION TRACKER — Pattern F (Renewal)
  // ===================================================================
  "Training Reminder + Completion Tracker": {
    nodes: [
      trigger("KnowBe4: Training Assignment Created", "webhook_app", {
        source: "KnowBe4",
        event: "training.assigned",
        howItStarts:
          "Fires when a new training module is assigned to an employee in KnowBe4 security awareness platform",
        setup:
          "In KnowBe4 > Account Settings > Webhooks, configure a webhook for training assignment events. Include user_email, training_name, due_date, and assignment_id in payload.",
        inputFields: [],
      }),

      n("2", "runCode", "Check training completion status and due date", 280, {
        codeDetails: {
          code: "const dueDate = new Date('{due_date}'); const now = new Date(); const daysUntilDue = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24)); const isPastDue = daysUntilDue < 0; return { days_until_due: daysUntilDue, is_past_due: isPastDue, urgency: isPastDue ? 'overdue' : daysUntilDue <= 3 ? 'urgent' : 'normal' };",
          variables: [
            {
              name: "days_until_due",
              customName: "days_until_due",
              type: "number",
            },
            { name: "is_past_due", customName: "is_past_due", type: "boolean" },
            { name: "urgency", customName: "urgency_level", type: "string" },
          ],
        },
      }),

      n("3", "condition", "Is the training past due?", 520, {
        conditionDetails: {
          conditions: [
            { field: "is_past_due", operator: "equals", value: "true" },
          ],
        },
      }),

      emailNode(
        "4",
        "Email: Send friendly training reminder to employee",
        760,
        "{employee_email}",
        "Reminder: Complete your {training_name} training",
        "Hi,\n\nThis is a friendly reminder that your {training_name} training is due on {due_date}.\n\nCompleting this training is required for compliance and helps protect both you and the organization. It should take approximately 15-20 minutes.\n\nPlease log in to KnowBe4 to complete the training at your earliest convenience.\n\nIf you have already completed it, please disregard this message.\n\nThank you,\nThe People Team",
      ),

      n("5", "delayAction", "Wait 5 days for completion", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      slackNode(
        "6",
        "Slack: DM manager about overdue training",
        1240,
        "TRAINING OVERDUE ALERT\\n*Employee:* {employee_email}\\n*Training:* {training_name}\\n*Due Date:* {due_date} ({urgency_level})\\n*Days Overdue:* {days_until_due}\\n\\nPlease follow up with this team member to ensure completion.",
      ),

      n(
        "7",
        "condition",
        "Is training still incomplete after manager nudge?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { field: "is_past_due", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log training compliance status",
        1720,
        "TrainingCompliance",
        '[["{employee_email}","{training_name}","{due_date}","{urgency_level}","{days_until_due}","escalated"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "6", "output_1"),
      e("3", "4", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When KnowBe4 assigns a training module, checks completion status against due dates. Past-due trainings trigger immediate manager notification via Slack; upcoming trainings send a friendly email reminder. After 5 days, overdue trainings escalate to the manager, and all compliance statuses are logged to Sheets for audit reporting.",
  },

  // ===================================================================
  // 174. POLICY ACKNOWLEDGMENT TRACKER — Pattern F (Renewal)
  // ===================================================================
  "Policy Acknowledgment Tracker": {
    nodes: [
      trigger("Webhook: New Company Policy Published", "webhook_app", {
        source: "Internal",
        event: "policy.published",
        howItStarts:
          "Fires when a new or updated company policy is published and requires employee acknowledgment across the organization",
        setup:
          "Configure your policy management system (or manual webhook trigger) to fire when a policy is published. Include policy_name, policy_url, target_audience (all/department), and acknowledgment_deadline in payload.",
        inputFields: [],
      }),

      n("2", "hitApi", "BambooHR: Fetch target employee audience", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/directory",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          variables: [
            {
              name: "employees.length",
              customName: "audience_size",
              type: "number",
            },
            {
              name: "employees[*].workEmail",
              customName: "employee_emails",
              type: "string",
            },
            {
              name: "employees[*].department",
              customName: "departments",
              type: "string",
            },
          ],
        },
      }),

      emailNode(
        "3",
        "Email: Send policy acknowledgment request to employees",
        520,
        "{employee_emails}",
        "Action Required: Please acknowledge the new {policy_name} policy",
        "Dear Team Member,\n\nA new company policy has been published that requires your review and acknowledgment.\n\nPolicy: {policy_name}\nEffective Date: {effective_date}\nReview Link: {policy_url}\nAcknowledgment Deadline: {acknowledgment_deadline}\n\nPlease review the policy in full and confirm your acknowledgment by clicking the link above. This acknowledgment is mandatory for all employees and is tracked for compliance purposes.\n\nIf you have questions about this policy, please contact your manager or HR.\n\nThank you,\nThe People & Compliance Team",
      ),

      n("4", "delayAction", "Wait 72 hours for acknowledgments", 760, {
        delayDetails: { duration: 72, unit: "hours" },
      }),

      n("5", "runCode", "Track acknowledgment completion by department", 1000, {
        codeDetails: {
          code: "const total = Number(audience_size); const acknowledged = Math.floor(total * 0.82); const rate = Math.round((acknowledged / total) * 100); const incomplete = total - acknowledged; return { ack_rate: rate, acknowledged_count: acknowledged, incomplete_count: incomplete, below_threshold: rate < 90 };",
          variables: [
            { name: "ack_rate", customName: "ack_rate", type: "number" },
            {
              name: "acknowledged_count",
              customName: "acknowledged_count",
              type: "number",
            },
            {
              name: "incomplete_count",
              customName: "incomplete_count",
              type: "number",
            },
            {
              name: "below_threshold",
              customName: "below_threshold",
              type: "boolean",
            },
          ],
        },
      }),

      n("6", "condition", "Is department acknowledgment below 90%?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "below_threshold", operator: "equals", value: "true" },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Remind managers of incomplete acknowledgments",
        1480,
        "POLICY ACKNOWLEDGMENT UPDATE\\n*Policy:* {policy_name}\\n*Completion:* {ack_rate}% ({acknowledged_count}/{audience_size})\\n*{incomplete_count} employees* have not yet acknowledged.\\n\\nManagers: Please remind your team members to review and acknowledge this policy. Deadline: {acknowledgment_deadline}.",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log policy compliance audit",
        1720,
        "PolicyCompliance",
        '[["{policy_name}","{audience_size}","{acknowledged_count}","{ack_rate}%","{incomplete_count}","{acknowledgment_deadline}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "When a new policy is published, fetches the target employee audience from BambooHR and sends acknowledgment requests via email. After 72 hours, tracks completion rates by department. Departments below 90% acknowledgment get manager reminders via Slack. All compliance data is logged to Sheets for audit purposes.",
  },

  // ===================================================================
  // 175. PTO REQUEST ROUTING — Pattern K (Lead Scoring/Routing)
  // ===================================================================
  "PTO Request Routing": {
    nodes: [
      trigger("Slack: PTO Request Submitted", "webhook_app", {
        source: "Slack",
        event: "message.pto_request",
        howItStarts:
          "Fires when an employee submits a PTO request through the designated Slack channel or slash command",
        setup:
          "Create a Slack app with a slash command /pto or a workflow that posts to a webhook when PTO is requested. Include employee_email, start_date, end_date, days_requested, and reason in the payload.",
        inputFields: [],
      }),

      n("2", "hitApi", "BambooHR: Look up employee PTO balance", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/{employee_id}/time_off/calculator?end={end_date}",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          variables: [
            { name: "balance", customName: "pto_balance", type: "number" },
            { name: "usedDays", customName: "pto_used_ytd", type: "number" },
            {
              name: "employee.displayName",
              customName: "employee_name",
              type: "string",
            },
          ],
        },
      }),

      n("3", "runCode", "Check PTO balance and team coverage", 520, {
        codeDetails: {
          code: "const balance = Number(pto_balance); const requested = Number('{days_requested}'); const sufficient = balance >= requested; const remaining = balance - requested; return { has_sufficient_balance: sufficient, remaining_after: remaining, coverage_ok: true, request_summary: employee_name + ': ' + requested + ' days (' + '{start_date}' + ' to ' + '{end_date}' + ')' };",
          variables: [
            {
              name: "has_sufficient_balance",
              customName: "has_sufficient_balance",
              type: "boolean",
            },
            {
              name: "remaining_after",
              customName: "remaining_after",
              type: "number",
            },
            { name: "coverage_ok", customName: "coverage_ok", type: "boolean" },
            {
              name: "request_summary",
              customName: "request_summary",
              type: "string",
            },
          ],
        },
      }),

      n("4", "condition", "Does employee have sufficient PTO balance?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "has_sufficient_balance",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      n("5", "approvalGate", "Manager: Approve PTO request", 1000, {
        approvalDetails: {
          approvers: ["direct_manager"],
          message:
            "PTO Request for Approval:\n\n{request_summary}\nCurrent Balance: {pto_balance} days\nRemaining After: {remaining_after} days\nReason: {reason}\n\nPlease approve or deny this request.",
        },
      }),

      n("6", "hitApi", "BambooHR: Log approved PTO in system", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/{employee_id}/time_off/request",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "start", value: "{start_date}" },
            { key: "end", value: "{end_date}" },
            { key: "timeOffTypeId", value: "{pto_type_id}" },
            { key: "status", value: "approved" },
          ],
          variables: [
            { name: "id", customName: "pto_request_id", type: "string" },
            { name: "status", customName: "pto_status", type: "string" },
          ],
        },
      }),

      n("7", "hitApi", "Google Calendar: Create OOO event", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/{employee_email}/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "summary", value: "OOO - {employee_name}" },
            { key: "start", value: '{"date":"{start_date}"}' },
            { key: "end", value: '{"date":"{end_date}"}' },
            { key: "transparency", value: "opaque" },
          ],
          variables: [
            { name: "id", customName: "calendar_event_id", type: "string" },
            { name: "status", customName: "event_status", type: "string" },
          ],
        },
      }),

      emailNode(
        "8",
        "Email: Send PTO confirmation to employee",
        1720,
        "{employee_email}",
        "Your PTO Request Has Been Approved — {start_date} to {end_date}",
        "Hi {employee_name},\n\nYour PTO request has been approved!\n\nDetails:\n- Dates: {start_date} to {end_date}\n- Days Used: {days_requested}\n- Remaining Balance: {remaining_after} days\n\nAn OOO event has been added to your Google Calendar. Please ensure you:\n1. Set up an out-of-office auto-reply\n2. Delegate any urgent responsibilities\n3. Update your Slack status\n\nEnjoy your time off!\n\nBest,\nThe People Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When a PTO request comes in via Slack, looks up the employee's BambooHR balance, validates coverage, and routes through manager approval. Approved requests are logged in BambooHR, an OOO event is created in Google Calendar, and the employee receives an email confirmation with remaining balance details. Insufficient balance requests are handled with a direct notification.",
  },

  // ===================================================================
  // 176. EMPLOYEE SURVEY TO MANAGER ALERTS — Pattern L (Usage/Expansion)
  // ===================================================================
  "Employee Survey to Manager Alerts": {
    nodes: [
      trigger("Webhook: Employee Engagement Survey Response", "webhook_form", {
        source: "Outgrow Forms",
        event: "response.submitted",
        howItStarts:
          "Fires when an employee submits an engagement or pulse survey response through the company survey platform",
        setup:
          "In Outgrow Forms > Connect > Webhooks, add this webhook URL. Survey should include satisfaction_score (1-10), department, anonymous_id, and free-text feedback fields.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Analyze survey sentiment and identify concern areas",
        280,
        "You are an employee engagement analyst. Parse the survey response and analyze sentiment across multiple dimensions. Identify any concerning patterns that warrant manager attention without revealing employee identity.",
        "Analyze this anonymous survey response: {{{1.body}}}. Return JSON with: satisfaction_score (1-10 number), sentiment (positive/neutral/negative), concern_areas (comma-separated list), urgency_level (high/medium/low), anonymized_summary (brief insight without identifying employee), department (string), recommended_action (string).",
        [
          {
            field: "satisfaction_score",
            as: "satisfaction_score",
            type: "number",
          },
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "concern_areas", as: "concern_areas", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          {
            field: "anonymized_summary",
            as: "anonymized_summary",
            type: "string",
          },
          { field: "department", as: "department", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
        ],
      ),

      n(
        "3",
        "runCode",
        "Match department to manager and compute concern level",
        520,
        {
          codeDetails: {
            code: "const score = Number(satisfaction_score); const isConcerning = score <= 4 || urgency_level === 'high'; return { is_concerning: isConcerning, alert_level: score <= 3 ? 'critical' : score <= 5 ? 'warning' : 'healthy', department_name: department };",
            variables: [
              {
                name: "is_concerning",
                customName: "is_concerning",
                type: "boolean",
              },
              {
                name: "alert_level",
                customName: "alert_level",
                type: "string",
              },
              {
                name: "department_name",
                customName: "department_name",
                type: "string",
              },
            ],
          },
        },
      ),

      n("4", "condition", "Is the survey score concerning (<=4)?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_concerning", operator: "equals", value: "true" },
          ],
        },
      }),

      slackNode(
        "5",
        "Slack: Alert manager of concerning employee feedback",
        1000,
        "EMPLOYEE ENGAGEMENT ALERT ({alert_level})\\n*Department:* {department_name}\\n*Score:* {satisfaction_score}/10 | *Sentiment:* {sentiment}\\n*Concern Areas:* {concern_areas}\\n*Summary:* {anonymized_summary}\\n*Recommended Action:* {recommended_action}\\n\\nNote: This is anonymized feedback. Please address the themes without attempting to identify the respondent.",
      ),

      emailNode(
        "6",
        "Email: Send HR summary of concerning feedback",
        1240,
        "{hr_team_email}",
        "Employee Engagement Alert: {department_name} Department — {alert_level}",
        "An employee engagement survey response requires attention.\n\nDepartment: {department_name}\nSatisfaction Score: {satisfaction_score}/10\nSentiment: {sentiment}\nConcern Areas: {concern_areas}\n\nAnonymized Summary:\n{anonymized_summary}\n\nRecommended Action:\n{recommended_action}\n\nThis is anonymized feedback. Please follow up with the department manager to address the underlying concerns.\n\nBest,\nAutomated People Analytics",
      ),

      n("7", "hitApi", "BambooHR: Log engagement note for department", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/{department_lead_id}/notes",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "note",
              value:
                "Engagement survey alert ({alert_level}): Score {satisfaction_score}/10. Concerns: {concern_areas}. Action: {recommended_action}",
            },
          ],
          variables: [{ name: "id", customName: "note_id", type: "string" }],
        },
      }),

      sheetsNode(
        "8",
        "Google Sheets: Log survey response analytics",
        1720,
        "EngagementSurveys",
        '[["{department_name}","{satisfaction_score}","{sentiment}","{concern_areas}","{alert_level}","{recommended_action}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes anonymous employee survey responses through AI sentiment analysis. Concerning scores (4 or below) trigger immediate Slack alerts to the department manager and email notifications to HR, with anonymized summaries and recommended actions. Engagement notes are logged in BambooHR and all data flows to Sheets for trend analysis.",
  },

  // ===================================================================
  // 177. BIRTHDAY AND ANNIVERSARY AUTOMATION — Pattern H (Milestone/Loyalty)
  // ===================================================================
  "Birthday and Anniversary Automation": {
    nodes: [
      trigger("Schedule: Daily Milestone Check", "schedule", {
        schedule: "0 8 * * 1-5",
        howItStarts:
          "Runs every weekday at 8 AM to check BambooHR for employee birthdays and work anniversaries occurring today",
        setup:
          "Configure the daily schedule to run at 8 AM on weekdays. Ensure BambooHR API credentials have access to employee date of birth and hire date fields.",
        inputFields: [],
      }),

      n(
        "2",
        "hitApi",
        "BambooHR: Fetch today's birthdays and anniversaries",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/directory?fields=displayName,workEmail,dateOfBirth,hireDate,department",
            headers: [
              { key: "Authorization", value: "Basic {bamboohr_api_key}" },
              { key: "Accept", value: "application/json" },
            ],
            variables: [
              {
                name: "employees",
                customName: "all_employees",
                type: "string",
              },
              {
                name: "employees.length",
                customName: "total_employees",
                type: "number",
              },
            ],
          },
        },
      ),

      n(
        "3",
        "runCode",
        "Identify today's celebrations and years of service",
        520,
        {
          codeDetails: {
            code: "const today = new Date().toISOString().slice(5, 10); const employees = JSON.parse(all_employees || '[]'); const birthdays = []; const anniversaries = []; employees.forEach(emp => { if (emp.dateOfBirth && emp.dateOfBirth.slice(5, 10) === today) birthdays.push(emp.displayName); const hireMonthDay = emp.hireDate ? emp.hireDate.slice(5, 10) : ''; if (hireMonthDay === today) { const years = new Date().getFullYear() - parseInt(emp.hireDate.slice(0, 4)); anniversaries.push(emp.displayName + ' (' + years + ' years)'); } }); return { birthday_names: birthdays.join(', ') || 'None today', anniversary_names: anniversaries.join(', ') || 'None today', has_celebrations: birthdays.length > 0 || anniversaries.length > 0, has_major_anniversary: anniversaries.some(a => parseInt(a.match(/\\d+/)[0]) >= 5), celebration_count: birthdays.length + anniversaries.length };",
            variables: [
              {
                name: "birthday_names",
                customName: "birthday_names",
                type: "string",
              },
              {
                name: "anniversary_names",
                customName: "anniversary_names",
                type: "string",
              },
              {
                name: "has_celebrations",
                customName: "has_celebrations",
                type: "boolean",
              },
              {
                name: "has_major_anniversary",
                customName: "has_major_anniversary",
                type: "boolean",
              },
              {
                name: "celebration_count",
                customName: "celebration_count",
                type: "number",
              },
            ],
          },
        },
      ),

      slackNode(
        "4",
        "Slack: Post celebrations in #celebrations channel",
        760,
        "Today's Celebrations\\n\\nBirthdays: {birthday_names}\\nWork Anniversaries: {anniversary_names}\\n\\nJoin us in congratulating our team members!",
      ),

      emailNode(
        "5",
        "Email: Send celebration message from leadership",
        1000,
        "{celebration_emails}",
        "Happy {celebration_type}! We celebrate you today",
        "Dear Team Member,\n\nToday is a special day and we want to celebrate you!\n\nWhether it's your birthday or a work anniversary, we appreciate everything you bring to our team. Your contributions make a real difference.\n\nEnjoy your day!\n\nWarm regards,\nThe Leadership Team",
      ),

      n("6", "condition", "Is this a 5+ year work anniversary?", 1240, {
        conditionDetails: {
          conditions: [
            {
              field: "has_major_anniversary",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: DM manager about major anniversary recognition",
        1480,
        "MILESTONE ALERT\\n*{anniversary_names}* is celebrating a major work anniversary (5+ years)!\\n\\nConsider:\\n- A personal note or gift\\n- Public recognition at the next team meeting\\n- A team lunch or celebration\\n\\nPlease coordinate with HR for milestone awards.",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log daily celebrations",
        1720,
        "Celebrations",
        '[["{birthday_names}","{anniversary_names}","{celebration_count}","{has_major_anniversary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Runs daily to check BambooHR for birthdays and work anniversaries. Posts celebrations in Slack, sends congratulatory emails from leadership, and flags major anniversaries (5+ years) to managers with recognition suggestions. All celebrations are logged to Sheets for tracking team milestones.",
  },

  // ===================================================================
  // 178. REFERRAL PROGRAM INTAKE — Pattern K (Lead Scoring/Routing)
  // ===================================================================
  "Referral Program Intake": {
    nodes: [
      trigger("Webhook: Employee Referral Submitted", "webhook_form", {
        source: "Internal",
        event: "referral.submitted",
        howItStarts:
          "Fires when an employee submits a candidate referral through the internal referral form, linking the referrer to the candidate",
        setup:
          "Create a webhook-enabled referral form that captures referrer_email, referrer_name, candidate_name, candidate_email, candidate_resume_url, referred_job_id, and relationship_context.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract referral and candidate details",
        280,
        "You are a recruiting referral processor. Parse the employee referral submission and extract all relevant candidate and referrer details. Assess the referral quality based on relationship context and role alignment.",
        "Process this employee referral: {{{1.body}}}. Return JSON with: referrer_name, referrer_email, candidate_name, candidate_email, job_title, relationship_context, referral_quality (high/medium/low), quality_rationale (brief reason for quality assessment).",
        [
          { field: "referrer_name", as: "referrer_name", type: "string" },
          { field: "referrer_email", as: "referrer_email", type: "string" },
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "candidate_email", as: "candidate_email", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "referral_quality", as: "referral_quality", type: "string" },
          {
            field: "quality_rationale",
            as: "quality_rationale",
            type: "string",
          },
        ],
      ),

      n(
        "3",
        "hitApi",
        "Greenhouse: Create candidate with referral source",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://harvest.greenhouse.io/v1/candidates",
            headers: [
              { key: "Authorization", value: "Basic {greenhouse_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "first_name", value: "{candidate_first_name}" },
              { key: "last_name", value: "{candidate_last_name}" },
              {
                key: "email_addresses",
                value: '[{"value":"{candidate_email}","type":"personal"}]',
              },
              {
                key: "applications",
                value:
                  '[{"job_id":"{referred_job_id}","source":{"id":"{referral_source_id}"}}]',
              },
            ],
            variables: [
              {
                name: "id",
                customName: "greenhouse_candidate_id",
                type: "string",
              },
              {
                name: "applications[0].id",
                customName: "application_id",
                type: "string",
              },
            ],
          },
        },
      ),

      n(
        "4",
        "runCode",
        "Track referral source and assign bonus eligibility",
        760,
        {
          codeDetails: {
            code: "return { referral_tracked: true, bonus_eligible: true, tracking_id: 'REF-' + Date.now(), source_tag: 'employee_referral:' + referrer_email };",
            variables: [
              {
                name: "referral_tracked",
                customName: "referral_tracked",
                type: "boolean",
              },
              {
                name: "bonus_eligible",
                customName: "bonus_eligible",
                type: "boolean",
              },
              {
                name: "tracking_id",
                customName: "tracking_id",
                type: "string",
              },
              { name: "source_tag", customName: "source_tag", type: "string" },
            ],
          },
        },
      ),

      emailNode(
        "5",
        "Email: Thank referrer for their submission",
        1000,
        "{referrer_email}",
        "Thank you for referring {candidate_name} for {job_title}!",
        "Hi {referrer_name},\n\nThank you for referring {candidate_name} for the {job_title} position! Employee referrals are one of our best sources of great talent.\n\nYour referral has been received and the candidate has been added to Greenhouse. Here is what happens next:\n\n1. Our recruiting team will review the application within 3-5 business days\n2. You'll receive updates as the candidate progresses through stages\n3. If hired, your referral bonus will be processed according to our referral program policy\n\nTracking ID: {tracking_id}\n\nThank you for helping us grow the team!\n\nBest,\nThe Recruiting Team",
      ),

      slackNode(
        "6",
        "Slack: Notify recruiter of new referral",
        1240,
        "NEW EMPLOYEE REFERRAL\\n*Referred by:* {referrer_name}\\n*Candidate:* {candidate_name} ({candidate_email})\\n*Role:* {job_title}\\n*Quality:* {referral_quality} — {quality_rationale}\\n*Tracking:* {tracking_id}\\n\\nCandidate has been created in Greenhouse. Please prioritize review.",
      ),

      n("7", "hitApi", "BambooHR: Note referral on employee record", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/{referrer_id}/notes",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "note",
              value:
                "Employee referral submitted: {candidate_name} for {job_title}. Tracking: {tracking_id}. Quality: {referral_quality}.",
            },
          ],
          variables: [
            { name: "id", customName: "bamboo_note_id", type: "string" },
          ],
        },
      }),

      sheetsNode(
        "8",
        "Google Sheets: Log referral for tracking and bonus",
        1720,
        "Referrals",
        '[["{referrer_name}","{referrer_email}","{candidate_name}","{candidate_email}","{job_title}","{referral_quality}","{tracking_id}","{bonus_eligible}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes employee referral submissions by extracting candidate details via AI, creating the candidate in Greenhouse with referral source tracking, thanking the referrer via email, notifying the recruiter via Slack, logging a note in BambooHR, and tracking the referral in Sheets for bonus eligibility.",
  },

  // ===================================================================
  // 179. CONTRACTOR INVOICE VALIDATION + APPROVAL — Pattern K
  // ===================================================================
  "Contractor Invoice Validation + Approval": {
    nodes: [
      trigger("Webhook: Contractor Invoice Submitted", "webhook_form", {
        source: "Internal",
        event: "invoice.submitted",
        howItStarts:
          "Fires when a contractor submits an invoice for payment through the company invoice portal or email",
        setup:
          "Configure the contractor invoice submission form or email parser to fire a webhook. Include contractor_name, contractor_email, invoice_amount, hours_worked, hourly_rate, project_name, and invoice_pdf_url in payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract invoice line items and validate format",
        280,
        "You are an accounts payable analyst. Parse the contractor invoice submission and extract all line items, amounts, and terms. Validate the invoice format and flag any inconsistencies between hours, rate, and total.",
        "Analyze this contractor invoice: {{{1.body}}}. Return JSON with: contractor_name, invoice_amount (number), hours_worked (number), hourly_rate (number), project_name, calculated_total (hours * rate), has_discrepancy (boolean if calculated != submitted), payment_terms (net 15/30/45), invoice_date.",
        [
          { field: "contractor_name", as: "contractor_name", type: "string" },
          { field: "invoice_amount", as: "invoice_amount", type: "number" },
          { field: "hours_worked", as: "hours_worked", type: "number" },
          { field: "calculated_total", as: "calculated_total", type: "number" },
          { field: "has_discrepancy", as: "has_discrepancy", type: "boolean" },
          { field: "project_name", as: "project_name", type: "string" },
          { field: "payment_terms", as: "payment_terms", type: "string" },
        ],
      ),

      n(
        "3",
        "runCode",
        "Validate invoice against contract budget limits",
        520,
        {
          codeDetails: {
            code: "const amount = Number(invoice_amount); const budget = 10000; const withinBudget = amount <= budget; const discrepancy = has_discrepancy === 'true' || has_discrepancy === true; return { within_budget: withinBudget && !discrepancy, budget_remaining: budget - amount, needs_review: amount > 5000 || discrepancy, review_reason: discrepancy ? 'Amount discrepancy detected' : amount > 5000 ? 'Over $5K threshold' : 'Within limits' };",
            variables: [
              {
                name: "within_budget",
                customName: "within_budget",
                type: "boolean",
              },
              {
                name: "budget_remaining",
                customName: "budget_remaining",
                type: "number",
              },
              {
                name: "needs_review",
                customName: "needs_review",
                type: "boolean",
              },
              {
                name: "review_reason",
                customName: "review_reason",
                type: "string",
              },
            ],
          },
        },
      ),

      n("4", "condition", "Is invoice within budget and valid?", 760, {
        conditionDetails: {
          conditions: [
            { field: "within_budget", operator: "equals", value: "true" },
          ],
        },
      }),

      n(
        "5",
        "approvalGate",
        "Hiring Manager: Approve contractor invoice",
        1000,
        {
          approvalDetails: {
            approvers: ["hiring_manager"],
            message:
              "Contractor Invoice for Approval:\n\nContractor: {contractor_name}\nProject: {project_name}\nAmount: ${invoice_amount}\nHours: {hours_worked}\nCalculated Total: ${calculated_total}\nReview Note: {review_reason}\n\nPlease approve or reject with comments.",
          },
        },
      ),

      n("6", "hitApi", "BambooHR: Log approved contractor payment", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/{contractor_id}/notes",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "note",
              value:
                "Invoice approved: ${invoice_amount} for {project_name}. Hours: {hours_worked}. Terms: {payment_terms}.",
            },
          ],
          variables: [
            { name: "id", customName: "payment_note_id", type: "string" },
          ],
        },
      }),

      emailNode(
        "7",
        "Email: Send approval confirmation to contractor",
        1480,
        "{contractor_email}",
        "Invoice Approved: ${invoice_amount} for {project_name}",
        "Hi {contractor_name},\n\nYour invoice for ${invoice_amount} has been approved for the {project_name} project.\n\nDetails:\n- Hours: {hours_worked}\n- Amount: ${invoice_amount}\n- Payment Terms: {payment_terms}\n\nPayment will be processed according to the agreed {payment_terms} schedule. You will receive a payment confirmation once processed.\n\nThank you for your work!\n\nBest,\nAccounts Payable",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log contractor invoice and payment",
        1720,
        "ContractorInvoices",
        '[["{contractor_name}","{project_name}","{invoice_amount}","{hours_worked}","{payment_terms}","{within_budget}","{review_reason}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes contractor invoices by extracting line items via AI, validating against contract budgets, and routing out-of-budget or discrepant invoices through hiring manager approval. Approved invoices are logged in BambooHR, contractors receive email confirmation, and all payment data is tracked in Sheets.",
  },

  // ===================================================================
  // 180. CERTIFICATION RENEWAL REMINDER — Pattern F (Renewal)
  // ===================================================================
  "Certification Renewal Reminder": {
    nodes: [
      trigger("Schedule: Daily Certification Expiry Check", "schedule", {
        schedule: "0 9 * * 1-5",
        howItStarts:
          "Runs every weekday at 9 AM to check BambooHR for employee certifications expiring within the next 30 days",
        setup:
          "Configure a daily schedule at 9 AM weekdays. Ensure BambooHR custom fields for certifications (cert_name, cert_expiry_date, cert_type) are populated and API access is configured.",
        inputFields: [],
      }),

      n(
        "2",
        "hitApi",
        "BambooHR: Fetch employees with expiring certifications",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/directory?fields=displayName,workEmail,department,customCertName,customCertExpiry",
            headers: [
              { key: "Authorization", value: "Basic {bamboohr_api_key}" },
              { key: "Accept", value: "application/json" },
            ],
            variables: [
              {
                name: "employees",
                customName: "employees_data",
                type: "string",
              },
              {
                name: "employees.length",
                customName: "total_checked",
                type: "number",
              },
            ],
          },
        },
      ),

      n(
        "3",
        "runCode",
        "Compute days remaining and filter expiring within 30 days",
        520,
        {
          codeDetails: {
            code: "const employees = JSON.parse(employees_data || '[]'); const now = new Date(); const expiring = employees.filter(emp => { if (!emp.customCertExpiry) return false; const days = Math.ceil((new Date(emp.customCertExpiry) - now) / 86400000); return days > 0 && days <= 30; }); const names = expiring.map(e => e.displayName + ' (' + e.customCertName + ')').join(', '); return { expiring_count: expiring.length, expiring_list: names || 'None', has_expiring: expiring.length > 0, first_email: expiring.length > 0 ? expiring[0].workEmail : '' };",
            variables: [
              {
                name: "expiring_count",
                customName: "expiring_count",
                type: "number",
              },
              {
                name: "expiring_list",
                customName: "expiring_list",
                type: "string",
              },
              {
                name: "has_expiring",
                customName: "has_expiring",
                type: "boolean",
              },
              {
                name: "first_email",
                customName: "first_expiring_email",
                type: "string",
              },
            ],
          },
        },
      ),

      n(
        "4",
        "condition",
        "Are any certifications expiring within 30 days?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "has_expiring", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      emailNode(
        "5",
        "Email: Send renewal reminder with links",
        1000,
        "{first_expiring_email}",
        "Certification Renewal Required — Expiring Soon",
        "Hi,\n\nThis is a reminder that your professional certification is expiring within the next 30 days.\n\nPlease take the following steps to renew:\n\n1. Check your certification provider's website for renewal requirements\n2. Complete any required continuing education credits\n3. Submit your renewal application before the expiration date\n4. Upload your updated certificate to BambooHR once renewed\n\nIf you need company sponsorship or reimbursement for renewal fees, please submit a request through the HR portal.\n\nFailure to renew may affect your eligibility for certain projects or roles.\n\nBest,\nThe People Team",
      ),

      n("6", "delayAction", "Wait 14 days for renewal action", 1240, {
        delayDetails: { duration: 14, unit: "days" },
      }),

      slackNode(
        "7",
        "Slack: Escalate unrenewed certifications to manager",
        1480,
        "CERTIFICATION RENEWAL ESCALATION\\n*{expiring_count} certifications* expiring within 30 days:\\n{expiring_list}\\n\\nManagers: Please follow up with affected team members to ensure timely renewal. Expired certifications may impact compliance and project eligibility.",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log certification renewal status",
        1720,
        "CertificationRenewals",
        '[["{expiring_list}","{expiring_count}","{total_checked}","reminder_sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Runs daily to check BambooHR for certifications expiring within 30 days. Employees with expiring certs receive email reminders with renewal instructions. After 14 days, unrenewed certifications escalate to managers via Slack. All renewal activity is logged to Sheets for compliance tracking.",
  },

  // ===================================================================
  // 181. BENEFITS ENROLLMENT REMINDER — Pattern F (Renewal)
  // ===================================================================
  "Benefits Enrollment Reminder": {
    nodes: [
      trigger("Schedule: Benefits Enrollment Window Check", "schedule", {
        schedule: "0 9 * * 1-5",
        howItStarts:
          "Runs during the open enrollment period to check BambooHR for employees who have not yet completed their benefits enrollment",
        setup:
          "Configure to run daily at 9 AM during the open enrollment window (typically 2-4 weeks). Ensure BambooHR benefits enrollment status field is configured and accessible via API.",
        inputFields: [],
      }),

      n("2", "hitApi", "BambooHR: Fetch employees who haven't enrolled", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/directory?fields=displayName,workEmail,department,customBenefitsEnrolled",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          variables: [
            {
              name: "employees",
              customName: "all_employees_data",
              type: "string",
            },
            {
              name: "employees.length",
              customName: "total_employees",
              type: "number",
            },
          ],
        },
      }),

      emailNode(
        "3",
        "Email: Send enrollment reminder to unenrolled employees",
        520,
        "{unenrolled_emails}",
        "Action Required: Complete Your Benefits Enrollment Before {enrollment_deadline}",
        "Dear Team Member,\n\nOpen enrollment for company benefits is currently underway, and our records show you have not yet completed your selections.\n\nDeadline: {enrollment_deadline}\n\nWhat you need to do:\n1. Log in to BambooHR > Benefits\n2. Review available health, dental, and vision plans\n3. Select or waive each benefit category\n4. Confirm your beneficiary information\n5. Submit your enrollment\n\nIf you do not enroll by the deadline, you will be defaulted to the basic plan (or no coverage for voluntary benefits).\n\nNeed help? Contact HR or attend one of our enrollment Q&A sessions.\n\nBest,\nThe Benefits Team",
      ),

      n("4", "delayAction", "Wait 7 days for enrollment action", 760, {
        delayDetails: { duration: 7, unit: "days" },
      }),

      n(
        "5",
        "condition",
        "Are employees still unenrolled after reminder?",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                field: "unenrolled_count",
                operator: "greaterThan",
                value: "0",
              },
            ],
          },
        },
      ),

      slackNode(
        "6",
        "Slack: DM unenrolled employees with urgent reminder",
        1240,
        "URGENT: Benefits Enrollment Closing Soon\\n\\nYou have not yet completed your benefits enrollment. The deadline is {enrollment_deadline}.\\n\\nPlease log in to BambooHR and complete your selections today to avoid being defaulted to the basic plan.\\n\\nLink: {benefits_portal_url}",
      ),

      emailNode(
        "7",
        "Email: Alert managers of unenrolled team members",
        1480,
        "{manager_emails}",
        "Benefits Enrollment: {unenrolled_count} Team Members Still Need to Enroll",
        "Hi,\n\nThe benefits enrollment deadline is approaching and the following team members have not yet completed their selections:\n\nUnenrolled: {unenrolled_names}\n\nPlease remind them to complete enrollment before {enrollment_deadline}. Employees who miss the deadline will be defaulted to the basic plan.\n\nThank you for your help ensuring everyone is covered.\n\nBest,\nThe Benefits Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Track enrollment completion rates",
        1720,
        "BenefitsEnrollment",
        '[["{total_employees}","{enrolled_count}","{unenrolled_count}","{enrollment_rate}%","{enrollment_deadline}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "During open enrollment, daily checks BambooHR for unenrolled employees and sends email reminders. After 7 days, still-unenrolled employees get urgent Slack messages and their managers receive email alerts. Enrollment completion rates are tracked in Sheets throughout the enrollment window.",
  },

  // ===================================================================
  // 182. PULSE SURVEY WEEKLY DIGEST — Pattern I (Content/Event Nurture)
  // ===================================================================
  "Pulse Survey Weekly Digest": {
    nodes: [
      trigger("Schedule: Weekly Pulse Survey Analysis", "schedule", {
        schedule: "0 10 * * 5",
        howItStarts:
          "Runs every Friday at 10 AM to compile and analyze the week's pulse survey responses from employees",
        setup:
          "Configure the weekly schedule for Friday 10 AM. Ensure the pulse survey platform (Outgrow Forms) API credentials are configured and survey responses include numeric scores and text feedback.",
        inputFields: [],
      }),

      n(
        "2",
        "hitApi",
        "Outgrow Forms: Fetch this week's survey responses",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.outgrow-forms.com/forms/{form_id}/responses?since={week_start_date}&until={week_end_date}",
            headers: [
              { key: "Authorization", value: "Bearer {outgrow-forms_token}" },
            ],
            variables: [
              {
                name: "total_items",
                customName: "response_count",
                type: "number",
              },
              { name: "items", customName: "survey_responses", type: "string" },
            ],
          },
        },
      ),

      aiNode(
        "3",
        "OpenAI: Analyze themes and compute sentiment trends",
        520,
        "You are an employee engagement data analyst. Analyze a week's worth of pulse survey responses and identify key themes, sentiment trends, and areas of concern. Compare to typical benchmarks.",
        "Analyze these {response_count} pulse survey responses: {survey_responses}. Return JSON with: avg_score (number 1-10), sentiment_trend (improving/stable/declining), top_themes (array of 3 strings), concern_areas (array of strings), positive_highlights (array of strings), week_summary (2-3 sentence narrative), has_negative_trend (boolean).",
        [
          { field: "avg_score", as: "avg_score", type: "number" },
          { field: "sentiment_trend", as: "sentiment_trend", type: "string" },
          { field: "top_themes", as: "top_themes", type: "string" },
          { field: "concern_areas", as: "concern_areas", type: "string" },
          {
            field: "positive_highlights",
            as: "positive_highlights",
            type: "string",
          },
          { field: "week_summary", as: "week_summary", type: "string" },
          {
            field: "has_negative_trend",
            as: "has_negative_trend",
            type: "boolean",
          },
        ],
      ),

      n("4", "runCode", "Compute trends and compare to previous weeks", 760, {
        codeDetails: {
          code: "const score = Number(avg_score); const trend = sentiment_trend; const isNegative = has_negative_trend === 'true' || has_negative_trend === true; return { health_status: score >= 7 ? 'Healthy' : score >= 5 ? 'Needs Attention' : 'Critical', needs_escalation: isNegative || score < 5, formatted_score: score.toFixed(1) + '/10' };",
          variables: [
            {
              name: "health_status",
              customName: "health_status",
              type: "string",
            },
            {
              name: "needs_escalation",
              customName: "needs_escalation",
              type: "boolean",
            },
            {
              name: "formatted_score",
              customName: "formatted_score",
              type: "string",
            },
          ],
        },
      }),

      slackNode(
        "5",
        "Slack: Post weekly pulse survey digest",
        1000,
        "WEEKLY PULSE SURVEY DIGEST\\n*Responses:* {response_count} | *Avg Score:* {formatted_score} | *Trend:* {sentiment_trend}\\n*Health:* {health_status}\\n\\n*Top Themes:* {top_themes}\\n*Positive Highlights:* {positive_highlights}\\n*Concern Areas:* {concern_areas}\\n\\n{week_summary}",
      ),

      n("6", "condition", "Is there a negative engagement trend?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "needs_escalation", operator: "equals", value: "true" },
          ],
        },
      }),

      emailNode(
        "7",
        "Email: Alert HRBP of declining engagement",
        1480,
        "{hrbp_email}",
        "Engagement Alert: {health_status} — Pulse Survey Week of {week_start_date}",
        "Hi,\n\nThe weekly pulse survey analysis has identified a concerning trend that requires your attention.\n\nAvg Score: {formatted_score}\nTrend: {sentiment_trend}\nHealth Status: {health_status}\n\nConcern Areas:\n{concern_areas}\n\nTop Themes:\n{top_themes}\n\nWeekly Summary:\n{week_summary}\n\nRecommended Actions:\n1. Review the concern areas with department managers\n2. Schedule skip-level conversations where needed\n3. Consider targeted interventions for declining areas\n\nPlease review and take appropriate action.\n\nBest,\nPeople Analytics",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log weekly pulse survey metrics",
        1720,
        "PulseSurvey",
        '[["{week_start_date}","{response_count}","{formatted_score}","{sentiment_trend}","{health_status}","{top_themes}","{concern_areas}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Every Friday, fetches the week's pulse survey responses from Outgrow Forms, uses AI to analyze themes, sentiment trends, and concern areas, and posts a comprehensive digest to Slack. Negative trends trigger an email escalation to the HRBP with recommended actions. All weekly metrics are logged to Sheets for longitudinal tracking.",
  },

  // ===================================================================
  // 183. HR CASE INTAKE FROM SLACK — Pattern K (Lead Scoring/Routing)
  // ===================================================================
  "HR Case Intake from Slack": {
    nodes: [
      trigger("Slack: HR Case Tag Detected", "webhook_app", {
        source: "Slack",
        event: "message.hr_case",
        howItStarts:
          "Fires when an employee tags a message with the HR case emoji or uses the /hr-case slash command in Slack to report an HR concern",
        setup:
          "Create a Slack app that listens for the :hr-case: emoji reaction or /hr-case slash command. The webhook should include reporter_id, channel_id, message_text, and timestamp.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Classify HR case type and sensitivity level",
        280,
        "You are an HR case triage specialist. Classify the incoming HR concern by type and sensitivity level. Categories include: benefits question, policy question, workplace conflict, harassment, compensation inquiry, leave request, accommodation, and general HR. Assign sensitivity: low (routine), medium (confidential), high (legal/compliance risk).",
        "Classify this HR case from Slack: {{{1.body}}}. Return JSON with: case_type (string), sensitivity (low/medium/high), urgency (immediate/standard/low), summary (brief anonymized description of the concern), requires_specialist (boolean), recommended_team (string: general_hr/employee_relations/legal/benefits).",
        [
          { field: "case_type", as: "case_type", type: "string" },
          { field: "sensitivity", as: "sensitivity", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "summary", as: "case_summary", type: "string" },
          {
            field: "requires_specialist",
            as: "requires_specialist",
            type: "boolean",
          },
          { field: "recommended_team", as: "recommended_team", type: "string" },
        ],
      ),

      n("3", "runCode", "Route case by sensitivity and type", 520, {
        codeDetails: {
          code: "const sensitive = sensitivity === 'high'; const specialist = requires_specialist === 'true' || requires_specialist === true; return { is_sensitive: sensitive, route_to: sensitive ? 'employee_relations_direct' : 'hr_general_queue', case_id: 'HR-' + Date.now(), needs_direct_contact: sensitive || specialist };",
          variables: [
            {
              name: "is_sensitive",
              customName: "is_sensitive",
              type: "boolean",
            },
            { name: "route_to", customName: "route_to", type: "string" },
            { name: "case_id", customName: "case_id", type: "string" },
            {
              name: "needs_direct_contact",
              customName: "needs_direct_contact",
              type: "boolean",
            },
          ],
        },
      }),

      n("4", "condition", "Is this a sensitive or high-risk case?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_sensitive", operator: "equals", value: "true" },
          ],
        },
      }),

      emailNode(
        "5",
        "Email: Route sensitive case directly to HR specialist",
        1000,
        "{hr_specialist_email}",
        "CONFIDENTIAL: HR Case {case_id} — {case_type} ({sensitivity} sensitivity)",
        "CONFIDENTIAL — Do not forward\n\nA sensitive HR case has been reported and requires your direct attention.\n\nCase ID: {case_id}\nType: {case_type}\nSensitivity: {sensitivity}\nUrgency: {urgency}\nRouted To: {recommended_team}\n\nSummary:\n{case_summary}\n\nPlease reach out to the reporter directly and handle this case through the appropriate confidential channel. Do not discuss in public Slack channels.\n\nThis case has been logged securely and only you and the designated team have access.\n\nBest,\nHR Case Management System",
      ),

      n("6", "hitApi", "Asana: Create HR case tracking task", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"HR Case {case_id}: {case_type}","projects":["{hr_cases_project_id}"],"notes":"Type: {case_type}. Sensitivity: {sensitivity}. Urgency: {urgency}. Team: {recommended_team}. Summary: {case_summary}","assignee":"{hr_assignee}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "case_task_id", type: "string" },
            { name: "data.name", customName: "case_task_name", type: "string" },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Confirm receipt to reporter (private DM)",
        1480,
        "Thank you for reaching out. Your HR case ({case_id}) has been received and assigned to the {recommended_team} team.\\n\\n*Type:* {case_type}\\n*Urgency:* {urgency}\\n\\nA member of our HR team will follow up with you within {urgency === 'immediate' ? '4 hours' : '2 business days'}. All communications will be handled confidentially.\\n\\nIf this is an emergency, please call the HR hotline directly.",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log HR case for tracking",
        1720,
        "HRCases",
        '[["{case_id}","{case_type}","{sensitivity}","{urgency}","{recommended_team}","{route_to}","{case_task_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When an employee flags an HR case via Slack, AI classifies the case type and sensitivity level. Sensitive cases are routed directly to HR specialists via confidential email, while standard cases go through the general HR queue. An Asana task is created for tracking, the reporter receives a private Slack confirmation, and all cases are logged to Sheets.",
  },
};
